import { sortedClips } from './timeline.js';
export function uebergangsZiel(timeline, playhead, selection, toleranz = 0.25) {
    // **Jede Bildspur, nicht nur die unterste** (18. September). Sascha: „jedes
    // andere schnittprogramm kann das auch überall / Mit den Übergängen“. Seit
    // der Export Übergänge auch oben rechnet, gibt es keinen Grund mehr, hier
    // nur unten zu suchen.
    const clips = timeline.tracks.filter((t) => t.kind === 'video').flatMap((t) => sortedClips(t));
    if (clips.length === 0)
        return null;
    // Ein Schnitt ist, wo ein Clip *auf seiner Spur* an einen vorigen
    // anschliesst. Deshalb wird je Spur gesucht und nicht in einem Topf.
    const schnitte = uebergangsStellen(timeline);
    const ziel = (stelle) => ({
        clipId: stelle.clipId,
        bei: stelle.bei,
        label: stelle.label,
    });
    for (const stelle of schnitte) {
        if (selection.includes(stelle.clipId))
            return ziel(stelle);
    }
    let beste = null;
    for (const stelle of schnitte) {
        const abstand = Math.abs(stelle.bei - playhead);
        if (abstand <= toleranz && (!beste || abstand < beste.abstand))
            beste = { stelle, abstand };
    }
    return beste ? ziel(beste.stelle) : null;
}
export function uebergangsStellen(timeline) {
    const fps = Math.max(1, timeline.settings.fps);
    const bildspuren = timeline.tracks.filter((t) => t.kind === 'video');
    const basisId = bildspuren[0]?.id;
    const raus = [];
    for (const spur of bildspuren) {
        const clips = sortedClips(spur);
        for (let i = 1; i < clips.length; i++) {
            const vor = clips[i - 1];
            const dieser = clips[i];
            // Eine Lücke davor ist kein Schnitt: da gibt es nichts zu überblenden.
            if (vor.start + vor.duration < dieser.start - 1 / fps)
                continue;
            // Und auf einer *oberen* Spur ist eine Umschliessung auch keiner
            // (7.74f): dort liegt oft ein Titel innerhalb einer Bauchbinde. Der
            // eine löst den anderen nicht ab, er liegt darüber -- kein Schnitt,
            // keine Marke.
            //
            // Auf der Basisspur gilt das nicht (7.74h, beim Nachlesen gefunden):
            // dort löst immer einer den anderen ab. Ein kurzer Clip hinter einem
            // langen, der mit einer Blende in ihn hineinragt -- A bis 10 s, B von
            // 9 bis 9,5 -- sieht nach derselben Regel aus wie der Titel, ist aber
            // ein Schnitt. Mit der Regel für alle Spuren verschwand dort die
            // Marke, und ein gesetzter Übergang war nicht mehr zu erreichen.
            if (spur.id !== basisId && vor.start + vor.duration > dieser.start + dieser.duration)
                continue;
            raus.push({
                clipId: dieser.id,
                vorClipId: vor.id,
                bei: dieser.start,
                label: dieser.label || 'Clip',
                vorLabel: vor.label || 'Clip',
                transition: dieser.transitionIn ? { kind: dieser.transitionIn.kind, duration: dieser.transitionIn.duration } : null,
                trackId: spur.id,
                spurName: spur.name || spur.id,
                aufBasis: spur.id === basisId,
            });
        }
    }
    // Die Basisspur zuerst, danach nach Zeit: dort lässt sich wirklich etwas
    // setzen, und was geht, gehört nach oben.
    return raus.sort((a, b) => (a.aufBasis === b.aufBasis ? a.bei - b.bei : a.aufBasis ? -1 : 1));
}
/**
 * Der Übergang **ist** die Überlappung (7.89).
 *
 * Zwei aneinanderstossende Clips haben zusammen kein Material für eine
 * Blende: was während der Blende zu sehen ist, muss von beiden gleichzeitig
 * kommen. Also müssen sie sich überlappen, und zwar um genau die Länge des
 * Übergangs. Der Export hat das bisher selbst zurechtgerückt -- er schob das
 * *Bild* des zweiten Clips nach vorn, nicht aber seinen Ton, und hängte am
 * Ende Schwarz an. Gefunden am 15. September beim Messen der Wischblenden.
 *
 * Hergestellt wird die Überlappung hier, beim Setzen: der zweite Clip rückt
 * so weit nach links, dass er um `dauer` in den vorigen hineinragt, und alles
 * dahinter **auf derselben Spur** rückt mit -- sonst risse ein Loch auf. Der
 * Rest des Schnitts bleibt stehen: ein Übergang im Bild soll nicht den Ton
 * darunter verschieben, um den niemand gebeten hat.
 *
 * `dauer` 0 heisst „kein Übergang“ und stellt den harten Schnitt wieder her.
 * Die Rechnung geht dabei von der *jetzigen* Überlappung aus, nicht von der
 * vorigen Dauer -- damit richtet schon ein einziges Setzen einen Schnitt
 * gerade, der vorher nicht überlappte.
 */
export function ueberlappungHerstellen(clips, clipId, dauer) {
    const dieser = clips.find((c) => c.id === clipId);
    if (!dieser)
        return [];
    // Der vorige auf der Spur: der letzte, der vor diesem anfängt.
    const vor = clips
        .filter((c) => c.id !== clipId && c.start < dieser.start)
        .sort((a, b) => a.start - b.start)
        .at(-1);
    if (!vor)
        return [];
    // Und er muss bis an diesen heranreichen. Liegt eine Lücke dazwischen, ist
    // das kein Schnitt: den hinteren Clip über die Lücke hinweg heranzuziehen
    // wäre etwas ganz anderes als eine Blende, und danach hat niemand gefragt.
    if (vor.start + vor.duration < dieser.start - 1e-9)
        return [];
    // Länger als einer der beiden kann eine Blende nicht sein -- sonst
    // verschwände ein Clip in ihr.
    const d = Math.max(0, Math.min(dauer, vor.duration, dieser.duration));
    const neuerStart = Math.max(0, vor.start + vor.duration - d);
    const versatz = neuerStart - dieser.start;
    if (Math.abs(versatz) < 1e-9)
        return [];
    return clips
        .filter((c) => c.id === clipId || c.start > dieser.start)
        .sort((a, b) => a.start - b.start)
        .map((c) => ({ clipId: c.id, start: Math.max(0, c.start + versatz) }));
}
/**
 * Die Lücken in den Bildspuren (7.105).
 *
 * Eine Lücke ist kein Schnitt, also gibt es dort keinen Übergang -- das Blatt
 * sagte das auch, aber es riet „zieh sie zusammen“ und liess die Arbeit beim
 * Menschen. Wer die Lücke kennt, kann sie auch schliessen.
 *
 * Seit dem 18. September auf *allen* Bildspuren: Übergänge gibt es überall,
 * also auch die Lücke, die einen verhindert.
 */
export function lueckenAufBasis(timeline) {
    const fps = Math.max(1, timeline.settings.fps);
    const raus = [];
    for (const spur of timeline.tracks.filter((t) => t.kind === 'video')) {
        const clips = sortedClips(spur);
        for (let i = 1; i < clips.length; i++) {
            const vor = clips[i - 1];
            const dieser = clips[i];
            const breite = dieser.start - (vor.start + vor.duration);
            // Dieselbe Grenze wie bei den Schnitten -- mit Luft gegen das Rechnen in
            // Gleitkommazahlen (4.04 - 4 ist minimal mehr als 1/25).
            if (breite <= 1 / fps + 1e-9)
                continue;
            raus.push({
                clipId: dieser.id,
                vorClipId: vor.id,
                label: dieser.label || 'Clip',
                vorLabel: vor.label || 'Clip',
                breite,
                trackId: spur.id,
            });
        }
    }
    return raus;
}
/**
 * Was sich bewegen muss, damit die Lücke vor `clipId` zugeht: dieser Clip und
 * alles dahinter auf derselben Spur, um die Breite der Lücke nach links.
 * Alles andere bliebe stehen und risse neue Lücken auf.
 */
export function lueckeSchliessen(clips, clipId) {
    const dieser = clips.find((c) => c.id === clipId);
    if (!dieser)
        return [];
    const vor = clips
        .filter((c) => c.id !== clipId && c.start < dieser.start)
        .sort((a, b) => a.start - b.start)
        .at(-1);
    if (!vor)
        return [];
    const breite = dieser.start - (vor.start + vor.duration);
    if (breite <= 1e-9)
        return [];
    return clips
        .filter((c) => c.id === clipId || c.start > dieser.start)
        .sort((a, b) => a.start - b.start)
        .map((c) => ({ clipId: c.id, start: Math.max(0, c.start - breite) }));
}
/**
 * **Die Übergänge, die eine Form haben** (7.147).
 *
 * Bis zum 22. September zeigte die Vorschau *jede* Art als Überblendung --
 * `playback.ts` rechnete eine Deckung und sonst nichts. Gemessen mit einer
 * Wischblende nach links, eine Sekunde in einen zwei Sekunden langen
 * Übergang: der Film zeigt oben links noch den alten Clip (rgb(29,121,58)),
 * die Vorschau eine halbe Mischung (rgb(115,86,68)) -- 86 von 255 auf
 * 99,8 % des Bildes.
 *
 * Hier stehen die acht, deren Maske **gemessen** ist: `xfade` schiebt eine
 * harte Kante durchs Bild, und wo sie steht, lässt sich an einer
 * Schwarz-Weiss-Blende ablesen. Die beiden Balken und die Iris sind dazu
 * gekommen (7.148): ihre Kante ist eine Rampe, und die Rampe ist dieselbe
 * `smoothstep`-Kennlinie, die ffmpeg benutzt -- auf den Wert genau.
 *
 * Die Diagonalen sind dazugekommen: ihre Iso-Linien sind **gekrümmt**,
 * weil die beiden Achsen als *Produkt* eingehen und nicht als Summe.
 *
 * Drei Arten stehen nicht in dieser Liste, und keine davon fehlt: die
 * beiden Schieber verschieben das Bild (`uebergangsSchub`), das Raster liest
 * es gröber ab (`uebergangsRaster`), und der Weg durch Schwarz oder Weiss
 * geht durch eine Farbe (`uebergangsDip`, 7.149). Eine Maske sagt, *wie
 * viel*; diese drei sagen *wo*, *woher* und *worüber*.
 */
export const UEBERGAENGE_MIT_FORM = [
    'wipeleft',
    'wiperight',
    'wipeup',
    'wipedown',
    // Dazugekommen am 22. September (7.148): die beiden Balken und die Iris.
    // Ihre Kante ist keine Kante, sondern eine Rampe -- ausgemessen an
    // Schwarz-Weiss-Blenden und gegen echtes ffmpeg auf den Wert genau.
    'horzopen',
    'vertopen',
    'circleopen',
    'circleclose',
    // Und die Diagonalen (7.148, zweiter Teil). Ihre Iso-Linien sind gekrümmt,
    // und das ist der Grund: die beiden Achsen gehen als **Produkt** ein und
    // nicht als Summe. Entlang x+y schwanken die Werte sonst von 115 bis 133.
    'diagtl',
    'diagbr',
    // Und die Uhr (7.148, dritter Teil): ein Zeiger, der zweieinhalb Halbkreise
    // dreht, mit einer Rampe von genau einem Radiant.
    'radial',
];
export function uebergangHatForm(art) {
    return UEBERGAENGE_MIT_FORM.includes(art);
}
/**
 * Wie viel vom **neuen** Clip an dieser Stelle zu sehen ist, 0 oder 1.
 *
 * `x` und `y` sind Anteile der Leinwand, `y` von **oben** -- so wie ffmpeg
 * das Bild durchläuft. Gemessen an einem 16x16-Bild bei einem Fortschritt
 * von 0,25: `wipeleft` zeigt den neuen Clip in den letzten drei Spalten
 * (also ab `x > 1 - p`), `wiperight` in den ersten fünf (`x <= p`). Die
 * beiden Zahlen sind verschieden, und das ist kein Rundungsfehler, sondern
 * das `>` gegen das `<=` in `vf_xfade.c`.
 *
 * Gefragt wird an der **Kante** des Punktes und nicht an seiner Mitte:
 * ffmpeg vergleicht ganze Zahlen (`x > W * (1 - p)`), und das ist dasselbe
 * wie der linke bzw. obere Rand. Der Shader fragt an der Mitte, also einen
 * halben Punkt weiter -- gemessen macht das im Film-Vergleich keinen
 * Unterschied (die Szenen liegen bei 2 bis 3 von 255), und eine Kante, die
 * um einen halben Punkt wandert, sieht niemand.
 *
 * Eine Art ohne Form gibt den Fortschritt selbst zurück: das ist die
 * Überblendung, die die Vorschau bisher überall gezeigt hat.
 */
export function uebergangsMaske(art, p, x, y, seiten = 1) {
    const f = Math.max(0, Math.min(1, p));
    switch (art) {
        case 'wipeleft':
            return x > 1 - f ? 1 : 0;
        case 'wiperight':
            return x <= f ? 1 : 0;
        case 'wipeup':
            return y > 1 - f ? 1 : 0;
        case 'wipedown':
            return y <= f ? 1 : 0;
        // Die weichen vier (7.148). `sanft` ist die Kennlinie, die ffmpeg
        // benutzt, und die Rampen sind ausgemessen: der Balken öffnet sich aus
        // der Mitte mit doppeltem Tempo, die Iris braucht ihr Drittel Anlauf.
        case 'horzopen':
            return sanft(2 * f - Math.abs(2 * y - 1));
        case 'vertopen':
            return sanft(2 * f - Math.abs(2 * x - 1));
        case 'circleopen':
            return sanft(3 * f - 0.5 - eckAbstand(x, y, seiten));
        case 'circleclose':
            return sanft(3 * f - 1.5 + eckAbstand(x, y, seiten));
        // **Jede Diagonale misst von ihrer eigenen Ecke** (7.148). `diagtl`
        // rechnet mit dem Abstand zur linken oberen (also `x` mal `y`), `diagbr`
        // mit dem zur rechten unteren. Kein Seitenverhältnis: gerechnet wird in
        // Anteilen und nicht in Bildpunkten -- gemessen, auf 32x32 wie auf 44x80.
        case 'diagtl':
            return sanft(2 * f - 1 + x * y);
        case 'diagbr':
            return sanft(2 * f - 1 + (1 - x) * (1 - y));
        // **Die Uhr** (7.148). Der Winkel wird als `atan2(dx, dy)` gemessen --
        // also von *unten* gezählt, mit dem Schnitt oben --, und **in
        // Bildpunkten**: ohne das Seitenverhältnis stünde der Zeiger im
        // Hochkant-Bild schief (gemessen 110 von 255 daneben). Der Zeiger dreht
        // über den ganzen Übergang um 2,5 Halbkreise, und die Rampe ist einen
        // Radiant breit.
        case 'radial':
            return sanft(Math.atan2((x - 0.5) * seiten, y - 0.5) + 2.5 * Math.PI * (f - 0.5));
        default:
            return f;
    }
}
/**
 * Die Kennlinie der weichen Übergänge -- `smoothstep`, wie ffmpeg sie
 * rechnet. Gemessen: über 32x32, 44x80 und 80x44 Punkte und sieben
 * Fortschritte trifft sie jeden Wert genau (einmal um eins daneben, und das
 * ist die Rundung).
 */
function sanft(t) {
    const u = Math.max(0, Math.min(1, t));
    return u * u * (3 - 2 * u);
}
/**
 * Wie weit ein Punkt von der Mitte weg ist, gemessen in Ecken: 0 in der
 * Mitte, 1 in jeder Ecke.
 *
 * **Und zwar in Bildpunkten und nicht in Anteilen** -- `hypotf(x - W/2,
 * y - H/2)` steht so in `vf_xfade.c`. Ohne das Seitenverhältnis wäre die
 * Iris im Hochkant-Bild ein Ei.
 */
function eckAbstand(x, y, seiten) {
    const dx = (x - 0.5) * seiten;
    const dy = y - 0.5;
    return Math.hypot(dx, dy) / Math.hypot(seiten / 2, 0.5);
}
/**
 * **Der Weg durch eine Farbe** (7.149): `dipToBlack` und `dipToWhite`.
 *
 * Diese beiden sind keine Maske. Sie blenden den alten Clip *aus* und den
 * neuen *ein*, und dazwischen steht für einen Moment nur die Farbe. Die
 * Vorschau zeigte stattdessen eine Überblendung -- Schwarz kam darin gar
 * nicht vor.
 *
 * Die beiden Gewichte sind an ffmpeg gemessen, und zwar einzeln: einmal von
 * Weiss nach Schwarz (dann steht dort nur der alte Anteil) und einmal
 * umgekehrt. Gemessen bei einem Zehntel: alt 0,447, neu 0,004; bei der
 * Hälfte: alt 0, neu 0,341; bei neun Zehnteln: alt 0, neu 0,898. Die Kurve
 * unten trifft jede dieser Zahlen.
 *
 * Die Farbe ist bei einem Fünftel erreicht und bleibt bis dahin, wo der neue
 * Clip anfängt -- der Weg *hinein* ist viermal so schnell wie der Weg
 * *hinaus*. Das ist keine Erfindung, sondern steht so in den Zahlen.
 */
export const DIP_PHASE = 0.2;
/** Dieselbe Kennlinie wie `smoothstep` in ffmpeg -- auch mit `kante0 > kante1`. */
function stufe(kante0, kante1, x) {
    if (kante0 === kante1)
        return x < kante0 ? 0 : 1;
    const t = Math.max(0, Math.min(1, (x - kante0) / (kante1 - kante0)));
    return t * t * (3 - 2 * t);
}
/** Welche Farbe ein Übergang durchläuft, oder `null`, wenn er keine durchläuft. */
export function dipFarbe(art) {
    if (art === 'dipToBlack')
        return '#000000';
    if (art === 'dipToWhite')
        return '#ffffff';
    return null;
}
/**
 * Wie viel vom alten und wie viel vom neuen Clip an dieser Stelle zu sehen
 * ist. Was fehlt, ist die Farbe: `1 - alt - neu`.
 */
export function uebergangsDip(art, p) {
    if (!dipFarbe(art))
        return null;
    const f = Math.max(0, Math.min(1, p));
    return { alt: (1 - f) * stufe(DIP_PHASE, 0, f), neu: f * stufe(0, 1 - DIP_PHASE, f) };
}
/**
 * **Die beiden Schieber** (7.148, vierter Teil): `slideleft` und
 * `slideright`.
 *
 * Sie sind keine Maske. Beide Bilder *wandern*, und zwar zusammen: das alte
 * hinaus, das neue hinterher. Eine Maske kann das nicht, ein Versatz schon --
 * und einen Versatz kennt die Leinwand ohnehin, denn jede Ebene hat eine
 * Lage.
 *
 * Gemessen an einem Verlauf (jede Spalte ein eigener Wert), 16 Punkte breit:
 * bei `slideleft` und einem Viertel steht die Spalte 4 der alten Ebene ganz
 * links -- sie ist also um ein Viertel der Breite nach links gerückt --, und
 * die Spalte 0 der neuen steht bei 12, also um drei Viertel nach rechts.
 * Zusammen decken die beiden das Bild genau ab, ohne Lücke und ohne
 * Überlappung; darum darf jede voll decken.
 */
export function uebergangsSchub(art, p) {
    const f = Math.max(0, Math.min(1, p));
    // `-0` wird zu `0` -- dieselbe Zahl, aber sie sieht in einer Prüfung anders
    // aus; dieselbe Regel wie bei den Farbrädern (`klemm`).
    const o = (x) => (x === 0 ? 0 : x);
    if (art === 'slideleft')
        return { alt: o(-f), neu: o(1 - f) };
    if (art === 'slideright')
        return { alt: o(f), neu: o(-(1 - f)) };
    return null;
}
/**
 * **Das Raster** (`pixelize`, 7.148, letzter Teil) -- und es ist weder eine
 * Maske noch ein Versatz.
 *
 * Die sechzehn Arten davor entscheiden je Punkt, *wie viel* vom neuen Clip
 * dort zu sehen ist. Das Raster entscheidet etwas anderes: *woher* der Punkt
 * geholt wird. Gemischt wird ganz gewöhnlich mit `p` -- aber beide Bilder
 * werden an derselben, gröber gerasterten Stelle abgelesen, und deshalb
 * werden sie zur Mitte hin klobig und danach wieder fein.
 *
 * **Am 23. September an ffmpeg ausgemessen**, mit einer Quelle, die ihre
 * eigene Stelle im Wert trägt (`X*4+Y/2`), gegen Schwarz geblendet. Damit
 * steht im Bild, aus welchem Punkt jeder Punkt geholt wurde:
 *
 *   d    = min(p, 1 - p)          -- symmetrisch um die Mitte
 *   dist = ceil(d * 50) / 50      -- ffmpeg rastert d auf Fünfzigstel
 *   Seite = dist * min(B, H) / 10 -- in Bildpunkten, quadratisch
 *
 * und abgelesen wird bei `floor((floor(x / Seite) + 0.5) * Seite)`, in
 * beiden Achsen. Punkt für Punkt gegen ffmpeg gehalten, 44x80, sieben
 * Stellen: **Abstand null** bei fünf, sonst eins (die Rundung).
 *
 * Zurückgegeben wird die Seite **als Anteil der kürzeren Bildseite**, also
 * `dist / 10`. Damit hängt sie nicht an der Auflösung: die Leinwand ist
 * kleiner als der Film, und das Raster hat auf beiden gleich viele Felder.
 * Null heisst „kein Raster“ -- am Anfang und am Ende des Übergangs.
 */
export function uebergangsRaster(art, p) {
    if (art !== 'pixelize')
        return 0;
    const f = Math.max(0, Math.min(1, p));
    const d = Math.min(f, 1 - f);
    return Math.ceil(d * 50) / 50 / 10;
}
/**
 * Die gerasterte Stelle in Bildpunkten -- dieselbe Rechnung, die der Shader
 * macht, damit der Prüfstand sie ohne Browser nachrechnen kann.
 *
 * `x` ist der Punkt (0 ist der erste), `seite` die Kantenlänge eines Feldes
 * in Punkten. Eine Seite von null oder weniger lässt die Stelle, wo sie ist.
 */
export function gerastert(x, seite) {
    if (!(seite > 0))
        return x;
    return Math.floor((Math.floor(x / seite) + 0.5) * seite);
}
//# sourceMappingURL=uebergang.js.map